import { useEffect } from 'react';

import type {
  FulfillmentOrderLine,
  OrderDetailsOrder,
  OrderDetailsOrderLine,
} from '@/types/orderDetailsOrder';

import { RETURN_ORDER_STATUS_MESSAGE_MAPPING } from '@/components/MyOrders/ReturnedOrders/ReturnOrderStatusStepper';
import type {
  FulfillmentFragment,
  LanguageCodeEnum,
  MyOrderDetailsQuery,
  OrderLine,
  UserFragment,
} from '@/graphql/saleor/api';
import { useRouter } from '@/hooks/useRouter';
import useSetState from '@/hooks/useSetState';
import { ProductType } from '@/lib/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { getOrderDisplayLinesFromOrder } from '@/lib/order';
import { paths } from '@/lib/paths';
import type { PaymentMethod } from '@/lib/payment/types';
import type { WithdrawalEligibility } from '@/lib/withdrawal/types';

const ORDER_LINE_RETURN_STATUS_KEY = 'item.status';
const ORDER_LINE_CANCEL_RETURN_COMMENT_KEY = 'item.cancel_return_comment';

export type OrderDetailsGuestSession = {
  eligibility: WithdrawalEligibility | null;
  guestLookup: {
    businessId: string;
    email: string;
  } | null;
  onEligibilityChange?: (eligibility: WithdrawalEligibility | null) => void;
  onGuestViewReturnDetails?: (fulfillmentId: string) => void;
  onStartReturn: () => void;
  onViewOriginalOrder?: () => void;
};

export interface OrderDetailsViewProps {
  guestSession?: OrderDetailsGuestSession;
  order?: OrderDetailsOrder;
  orderId?: string;
  paymentMethod?: PaymentMethod[] | PaymentMethod;
  returnOrderId?: string;
  type?: string;
}

export function resolveOrderDetailsDisplayLines(
  order: OrderDetailsOrder,
  fulfillment?: FulfillmentFragment,
): OrderDetailsOrderLine[] {
  if (fulfillment) {
    return (fulfillment.lines ?? []).flatMap((returnLine) => {
      const orderLine: FulfillmentOrderLine | null | undefined =
        returnLine.orderLine;
      return orderLine ? [orderLine] : [];
    });
  }
  return getOrderDisplayLinesFromOrder(order);
}

export function getOrderLineReturnStatusAndFulfillmentId(
  orderLine: Pick<OrderDetailsOrderLine, 'id' | 'metadata'>,
  order: OrderDetailsOrder,
) {
  const fulfillment = order.fulfillments?.find(
    (candidate) =>
      candidate.status === 'RETURNED' &&
      candidate.lines?.some((line) => line?.orderLine?.id === orderLine.id),
  );

  if (!fulfillment) {
    return {
      returnStatus: undefined,
      fulfillmentId: undefined,
      cancelReturnComment: undefined,
    };
  }

  const lineReturnStatus = getMetaValue(
    orderLine.metadata ?? [],
    ORDER_LINE_RETURN_STATUS_KEY,
  );

  const returnStatus =
    lineReturnStatus && lineReturnStatus in RETURN_ORDER_STATUS_MESSAGE_MAPPING
      ? lineReturnStatus
      : (fulfillment.returnStatus ?? undefined);

  const cancelReturnComment = getMetaValue(
    orderLine.metadata ?? [],
    ORDER_LINE_CANCEL_RETURN_COMMENT_KEY,
  );

  return {
    returnStatus,
    fulfillmentId: fulfillment.id,
    cancelReturnComment: cancelReturnComment?.trim() || undefined,
  };
}

export function resolveOrderLineReturnFields(
  orderLine: Pick<OrderDetailsOrderLine, 'id' | 'metadata'> | null | undefined,
  order: OrderDetailsOrder,
  options: { fulfillmentId?: string; isReturnHistory: boolean },
) {
  if (!orderLine?.id) {
    return {
      fulfillmentId: '',
      returnStatus: '',
      cancelReturnComment: '',
    };
  }

  const res = getOrderLineReturnStatusAndFulfillmentId(orderLine, order);

  return {
    fulfillmentId: options.isReturnHistory
      ? (options.fulfillmentId ?? res.fulfillmentId ?? '')
      : (res.fulfillmentId ?? ''),
    returnStatus: res.returnStatus ?? '',
    cancelReturnComment: res.cancelReturnComment ?? '',
  };
}

export function getReturnFulfillmentFromOrder(
  order: MyOrderDetailsQuery['order'] | null | undefined,
): FulfillmentFragment | undefined {
  const list = order?.fulfillments ?? [];
  const returned = list.filter((f) => f?.status === 'RETURNED');
  if (returned.length === 0) {
    return undefined;
  }
  if (returned.length === 1) {
    return returned[0] as FulfillmentFragment;
  }
  return [...returned].sort((a, b) => {
    const da = new Date(a.created ?? 0).getTime();
    const db = new Date(b.created ?? 0).getTime();
    return db - da;
  })[0] as FulfillmentFragment;
}
type OrderDetailsState = {
  fulfillment?: FulfillmentFragment;
  loading: boolean;
  order?: OrderDetailsOrder;
  paymentMethod?: PaymentMethod[] | PaymentMethod;
};

export const useOrderDetails = ({
  languageCode,
  order,
  orderId,
  paymentMethod,
  returnOrderId,
  user,
}: OrderDetailsViewProps & {
  languageCode: LanguageCodeEnum;
  user?: Partial<UserFragment> | null;
}) => {
  const { replace } = useRouter();
  const [state, setState] = useSetState<OrderDetailsState>({
    order: order,
    paymentMethod: paymentMethod,
    loading: false,
  });
  async function getOrderDetailsById(
    orderId: string,
    languageCode: LanguageCodeEnum,
    email?: string,
    userId?: string,
  ) {
    const req = await fetch(
      paths.api.order.getDetailsById.asPath({
        id: orderId,
        query: {
          email: email ?? '',
          userId: userId ?? '',
          languageCode: languageCode,
        },
      }),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
    );

    if (req.ok) {
      const respData = await req.json();
      return {
        order: respData.data.order as MyOrderDetailsQuery['order'],
        paymentMethod: respData.paymentMethod as PaymentMethod[],
      };
    } else {
      if (req.status === 404) {
        replace(paths.notFound.url());
      }
      return {};
    }
  }
  const fetchOrderData = async () => {
    if (state.loading) {
      return;
    }
    setState({
      loading: true,
    });
    const data = await getOrderDetailsById(
      orderId!,
      languageCode,
      user?.email,
      user?.id,
    );
    if (data?.order && data?.paymentMethod) {
      const fulfillmentForReturn = returnOrderId
        ? (data.order?.fulfillments?.find(
            (fulfillment) => fulfillment.id === returnOrderId,
          ) as FulfillmentFragment | undefined)
        : undefined;

      setState({
        order: data.order,
        paymentMethod: data.paymentMethod,
        loading: false,
        fulfillment: fulfillmentForReturn,
      });
    } else {
      setState({ loading: false });
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderData();
    }
  }, [orderId, user?.id, user?.email, languageCode]);

  useEffect(() => {
    if (!state.order || state.loading) {
      return;
    }
    if (!returnOrderId) {
      setState({ fulfillment: undefined });
      return;
    }
    const match = state.order.fulfillments?.find((f) => f.id === returnOrderId);
    setState({
      fulfillment: match as FulfillmentFragment | undefined,
    });
  }, [returnOrderId, state.order, state.loading]);

  const fulfillmentFromOrder =
    order && returnOrderId && !state.fulfillment
      ? (order.fulfillments?.find((f) => f.id === returnOrderId) as
          | FulfillmentFragment
          | undefined)
      : undefined;
  const fulfillment = state.fulfillment ?? fulfillmentFromOrder;

  return {
    ...state,
    fulfillment,
  };
};

type ClassifyableOrderLine = OrderDetailsOrderLine | OrderLine;

type LineMap = { [key: string]: ClassifyableOrderLine };

type ClassifiedLines = Record<
  `${ProductType}Lines`,
  ClassifyableOrderLine[]
> & {
  lineMap: LineMap;
};

const classifyLines = (lines: ClassifyableOrderLine[]): ClassifiedLines => {
  const orderLinesByProductType: Record<ProductType, ClassifyableOrderLine[]> =
    {};
  const lineMap: LineMap = {};

  for (const type of Object.values(ProductType)) {
    orderLinesByProductType[type] = [];
  }

  for (const line of lines) {
    const productType = line.variant?.product.productType.slug as ProductType;
    if (productType && orderLinesByProductType[productType]) {
      orderLinesByProductType[productType].push(line);
    }
    lineMap[line.id] = line;
  }

  return {
    eyewearLines: orderLinesByProductType[ProductType.Eyewear],
    watchLines: orderLinesByProductType[ProductType.Watch],
    strapLines: orderLinesByProductType[ProductType.Strap],
    buckleLines: orderLinesByProductType[ProductType.Buckle],
    warrantyLines: orderLinesByProductType[ProductType.Warranty],
    toolLines: orderLinesByProductType[ProductType.Tool],
    giftLines: orderLinesByProductType[ProductType.Gift],
    digitalLines: orderLinesByProductType[ProductType.Digital],
    lineMap,
    engravingLines: orderLinesByProductType[ProductType.Engraving],
  };
};

export function sortLines(
  lines: OrderDetailsOrderLine[],
  groupedByWatch?: boolean,
): OrderDetailsOrderLine[];
export function sortLines(
  lines: OrderLine[],
  groupedByWatch?: boolean,
): OrderLine[];
export function sortLines(
  lines: ClassifyableOrderLine[],
  groupedByWatch = false,
): ClassifyableOrderLine[] {
  const {
    buckleLines,
    digitalLines,
    engravingLines,
    eyewearLines,
    giftLines,
    strapLines,
    toolLines,
    warrantyLines,
    watchLines,
  } = classifyLines(lines);

  const used = new Set<string>();

  const getRelated = (src: ClassifyableOrderLine[], watchId: string) => {
    const related = src.filter(
      (l) =>
        'relatedProductWatchId' in l && l.relatedProductWatchId === watchId,
    );
    related.forEach((l) => used.add(l.id));
    return related;
  };

  const watchGroups = watchLines.flatMap((w) => {
    used.add(w.id);
    if (!groupedByWatch) return [w];
    return [
      w,
      ...getRelated(warrantyLines, w.id),
      ...getRelated(engravingLines, w.id),
    ];
  });

  const tailGroups = [
    ...strapLines,
    ...buckleLines,
    ...warrantyLines.filter((l) => !used.has(l.id)),
    ...toolLines,
    ...giftLines,
    ...digitalLines,
    ...engravingLines.filter((l) => !used.has(l.id)),
  ];

  return [...eyewearLines, ...watchGroups, ...tailGroups];
}
